import React, { useEffect, useState } from 'react';
import { helpMeChooseData } from '../../components/bearing-search/api/api';
import { text } from '../data';
import { generateId, purgeHtml } from '../utils/engineering-tools';
import { BearingCharacteristicGrade } from '../../components/bearing-search/types/types';

type AssistedTypeOption = {
	id: string;
	label: string;
	html: string;
	selected: boolean;
	partTypes: string[];
	characteristics: Record<string, string>;
};

interface BearingTypeAssistanceProps {
	onCancel: () => void;
	onApply: ( types: string[] ) => void;
}

let isModified = false;

const BearingTypeAssistance: React.FC<BearingTypeAssistanceProps> = ( {
	preselected,
	isOpen,
	onApply,
	onCancel,
} ) => {
	const [ assistedTypes, setAssistedTypes ] = useState<AssistedTypeOption[]>( [] );

	useEffect( () => {
		const types = helpMeChooseData.bearingTypes.map( ( type ) => {
			const typeLabel = purgeHtml( type.label );
			const typeId = generateId( typeLabel );

			return {
				id: typeId,
				label: typeLabel,
				html: type.label,
				selected: false,
				partTypes: type.part_types ? type.part_types.split( ',' ) : [],
				characteristics: type.characteristics,
			} as AssistedTypeOption;
		} );

		setAssistedTypes( types );
	}, [] );

	const toggleAssistedType = ( index: number ) => {
		isModified = true;
		const types = [ ...assistedTypes ];

		if ( ! types[ index ] ) {
			return;
		}

		types[ index ].selected = ! types[ index ].selected;
		setAssistedTypes( types );
	};

	const preselectAssistedTypes = () => {
		if (!isModified) {
			assistedTypes.forEach(function (type, index) {
				type.partTypes.forEach(function(partTypes) {
					const types = [ ...assistedTypes ];
					if (partTypes === "CRB_2Row") return;
					if (preselected.includes(partTypes)) {
						types[ index ].selected = true;
					} else {
						types[ index ].selected = false;
					}
				})
			});
		}
 	};

	const apply = () => {
		const partTypes: string[] = [];

		assistedTypes
			.filter( ( type ) => type.selected )
			.forEach( ( type ) => {
				for ( const partType of type.partTypes ) {
					if ( ! partTypes.includes( partType ) ) {
						partTypes.push( partType );
					}
				}
			} );

		onApply( partTypes );
		isModified = false;
		reset();
	};

	const cancel = () => {
		reset();
		onCancel();
	};

	const reset = () => {
		const types = assistedTypes.map( ( type ) => {
			return {
				...type,
				selected: false,
			} as AssistedTypeOption;
		} );

		setAssistedTypes( types );
	};

	if (isOpen) {
		preselectAssistedTypes()
	}

	if (!isOpen) {
		isModified = false;
	}

	return (
		<div className="c-help-me-choose">
			<h3>{helpMeChooseData.title || ''}</h3>
			<p dangerouslySetInnerHTML={{ __html: helpMeChooseData.description }}></p>

			<div className="c-help-me-choose__body">
				<table>
					<thead>
						<tr>
							<th scope="col">{text.characteristic}</th>
							{assistedTypes.map( ( type, index ) => {
								return (
									<th key={`help_me_choose_type_${ type.id }`}>
										<div className="m-checkbox m-checkbox--add">
											<input
												id={`bearing_type_${ type.id }`}
												className="m-checkbox__input"
												type="checkbox"
												checked={type.selected}
												onChange={() => toggleAssistedType( index )}
												aria-label={type.label}
											/>
											<label
												className="m-checkbox__label"
												htmlFor={`bearing_type_${ type.id }`}
												dangerouslySetInnerHTML={{ __html: type.html }}
											></label>
										</div>
									</th>
								);
							} )}
						</tr>
					</thead>
					<tbody>
						{Object.keys( helpMeChooseData.characteristics ).map(
							( characteristic ) => {
								return (
									<tr key={`help_me_choose_row_${ characteristic }`}>
										<th scope="row">
											{helpMeChooseData.characteristics[ characteristic ]}
										</th>

										{assistedTypes.map( ( type ) => {
											const grade = type.characteristics[ characteristic ];

											const cssClass: Record<
												BearingCharacteristicGrade,
												string
											> = {
												excellent: 'c-help-me-choose__data--positive',
												good: 'c-help-me-choose__data--positive',
												fair: '',
												poor: 'c-help-me-choose__data--negative',
												unsuitable: 'c-help-me-choose__data--negative',
											};

											return (
												<td
													className={`c-help-me-choose__data ${ cssClass[ grade ] }`}
													key={`help_me_choose_${ type.label }_${ characteristic }`}
												>
													{helpMeChooseData.performances[ grade ]}
												</td>
											);
										} )}
									</tr>
								);
							},
						)}
					</tbody>
				</table>
			</div>

			<div className="c-help-me-choose__actions">
				<button
					className="btn btn--primary"
					disabled={! assistedTypes.some( ( type ) => type.selected ) && !isModified}
					onClick={() => apply()}
				>
					{text.apply}
				</button>
				<button className="btn btn--secondary" onClick={() => cancel()}>
					{text.cancel}
				</button>
			</div>
		</div>
	);
};

export default BearingTypeAssistance;
